import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RequestTypes, RequestTypesKeys } from '@app/enums/RequestTypes';
import { PotentialDuplicateRequestDto } from '@app/generated-api';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { ModalResult } from '@app/v2/enums/modal-result.enum';
import { RequestType } from '@app/v2/enums/request-types.enum';
import {
    MdsButtonComponent,
    MdsCheckboxComponent,
    MdsDialogActionsComponent,
    MdsDialogComponent,
    MdsDialogContentComponent,
} from '@medpacesoftwaredevelopment/designsystem';

export interface DuplicateDialogData {
    potentialDuplicates: PotentialDuplicateRequestDto[];
}

export interface DuplicateDialogResult {
    result: ModalResult;
    selectedExpenseIds: { expenseId: number; requestType: RequestType }[];
}

@Component({
    selector: 'medpace-duplicate-dialog',
    templateUrl: './duplicate-dialog.component.html',
    imports: [
        MdsDialogComponent,
        MdsDialogContentComponent,
        MdsDialogActionsComponent,
        MdsButtonComponent,
        MdsCheckboxComponent,
        dateFormatPipe,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DuplicateDialogComponent {
    private dialogRef = inject(MatDialogRef<DuplicateDialogComponent>);
    public readonly data = signal<DuplicateDialogData>(inject(MAT_DIALOG_DATA));
    hasMoreThanOneDuplicate = computed(() => this.data().potentialDuplicates.length > 1);
    public selectedItems = signal<Set<number>>(new Set());
    public hasSelection = computed(() => this.selectedItems().size > 0);

    toggleItem(index: number) {
        const current = new Set(this.selectedItems());
        if (current.has(index)) {
            current.delete(index);
        } else {
            current.add(index);
        }
        this.selectedItems.set(current);
    }

    getPrefix(requestType: string): string {
        switch (requestType) {
            case RequestTypesKeys.STIPEND:
                return 'S';
            case RequestTypesKeys.REIMBURSEMENT:
                return 'R';
            case RequestTypesKeys.TRAVEL:
                return 'T';
            default:
                return '';
        }
    }

    getEditRoute(duplicate: PotentialDuplicateRequestDto): string {
        const basePath = `/studies/${duplicate.studyId}/sites/${duplicate.siteId}/patients/${duplicate.patientId}/requests/${duplicate.requestId}/edit`;

        const queryParams = new URLSearchParams();

        queryParams.set(
            'payment-travel',
            duplicate.requestType === RequestTypesKeys.TRAVEL ? RequestTypes.TRAVEL : RequestTypes.PAYMENT
        );

        if (duplicate.requestType === RequestTypesKeys.REIMBURSEMENT) {
            queryParams.set('stipend-oop', 'out-of-pocket');
        } else if (duplicate.requestType === RequestTypesKeys.STIPEND) {
            queryParams.set('stipend-oop', 'stipend');
        }

        return `${basePath}?${queryParams.toString()}`;
    }

    confirm() {
        const duplicates = this.data().potentialDuplicates;
        const selected =
            duplicates.length === 1
                ? duplicates.map((d) => ({ expenseId: d.expenseId, requestType: d.requestType }))
                : duplicates
                      .filter((_, i) => this.selectedItems().has(i))
                      .map((d) => ({ expenseId: d.expenseId, requestType: d.requestType }));

        this.dialogRef.close({
            result: ModalResult.Okay,
            selectedExpenseIds: selected,
        } as DuplicateDialogResult);
    }

    cancel() {
        this.dialogRef.close({
            result: ModalResult.Cancel,
            selectedExpenseIds: [],
        } as DuplicateDialogResult);
    }
}
